import React, { forwardRef, useEffect, useState } from 'react';

const FRAME_W = 1813;
const FRAME_H = 1066;
const VIDEO_W = 1705;
const VIDEO_H = 960;
const BLEED_PX = 5; // slight overscan under the mask to avoid 1–3px gaps at edges

const LEFT_PX = Math.round((FRAME_W - VIDEO_W) / 2);  // ~54
const TOP_PX  = Math.round((FRAME_H - VIDEO_H) / 2);   // ~53

const VIDEO_ASSET_MAP = {
  '/assets/videos/Smart_Expense_v002.mp4': {
    mobile: '/assets/videos/Smart_Expense_v002_mobile.mp4',
    poster: '/assets/videos/Smart_Expense_v002_poster.jpg',
  },
  '/assets/videos/OOP_feature_v003.mp4': {
    mobile: '/assets/videos/OOP_feature_v003_mobile.mp4',
    poster: '/assets/videos/OOP_feature_v003_poster.jpg',
  },
  '/assets/videos/Auto_categorize_row2.mp4': {
    mobile: '/assets/videos/Auto_categorize_row2_mobile.mp4',
    poster: '/assets/videos/Auto_categorize_row2_poster.jpg',
  },
  '/assets/videos/CT600_AI_Filing_v002.mp4': {
    mobile: '/assets/videos/CT600_AI_Filing_v002_mobile.mp4',
    poster: '/assets/videos/CT600_AI_Filing_v002_poster.jpg',
  },
  '/assets/videos/Context_Aware_chat_v002.mp4': {
    mobile: '/assets/videos/Context_Aware_chat_v002_mobile.mp4',
    poster: '/assets/videos/Context_Aware_chat_v002_poster.jpg',
  },
  '/assets/videos/Charity_overview.mp4': {
    mobile: '/assets/videos/Charity_overview_mobile.mp4',
    poster: '/assets/videos/Charity_overview_poster.jpg',
  },
};

function toPct(px, total) {
  return (px / total) * 100;
}

const DemoVideoFrame = forwardRef(function DemoVideoFrame({
  src = '/assets/videos/Smart_Expense_v002.mp4',
  className = '',
  autoPlayMutedLoop = true,
  inset = { left: LEFT_PX, top: TOP_PX, width: VIDEO_W, height: VIDEO_H },
  fit = 'cover',
  useMaskInset = true,
}, ref) {
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const { left, top, width, height } = inset;
  const mappedAssets = VIDEO_ASSET_MAP[src] || {};

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobileViewport(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  const selectedSrc = isMobileViewport && mappedAssets.mobile ? mappedAssets.mobile : src;
  const selectedPoster = mappedAssets.poster || undefined;

  // Slightly expand the video under the transparent mask so the cutout is fully filled.
  const effective = {
    left: left - BLEED_PX,
    top: top - BLEED_PX,
    width: width + (BLEED_PX * 2),
    height: height + (BLEED_PX * 2),
  };

  const styles = useMaskInset
    ? {
        left: `${toPct(effective.left, FRAME_W)}%`,
        top: `${toPct(effective.top, FRAME_H)}%`,
        width: `${toPct(effective.width, FRAME_W)}%`,
        height: `${toPct(effective.height, FRAME_H)}%`,
        objectFit: fit,
      }
    : {
        left: '0%',
        top: '0%',
        width: '100%',
        height: '100%',
        objectFit: fit,
      };

  return (
    <div
      className={`relative w-full ${className}`}
      style={{ aspectRatio: `${FRAME_W} / ${FRAME_H}`, maxWidth: '100%' }}
    >
      {/* Underlay (includes outer shadow in the PNG) */}
      <img
        src="/assets/images/Neevii_demo_screen_BG_light.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
      />

      {/* Video layer: masked by the container PNG (solid pixels show video) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          WebkitMaskImage: "url('/assets/images/Neevii_demo_container_light.png')",
          maskImage: "url('/assets/images/Neevii_demo_container_light.png')",
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
        }}
      >
        <video
          src={selectedSrc}
          preload="metadata"
          poster={selectedPoster}
          playsInline
          muted={autoPlayMutedLoop}
          loop={autoPlayMutedLoop}
          autoPlay={autoPlayMutedLoop}
          controls={false}
          className="absolute"
          style={styles}
          ref={ref}
        />
      </div>
    </div>
  );
});

export default DemoVideoFrame;

